/**
 * Muted auto-advance for the Instagram/social video strip.
 * When the row scrolls into view, plays its videos one at a time
 * (always muted, never simultaneously), advancing on each video's
 * `ended` event and looping back to the first. Pauses when the row
 * scrolls out of view. A visitor tapping any poster stops the
 * auto-sequence and hands control back to Dawn's own deferred-media
 * click-to-play (unmuted, since it's a direct user gesture).
 */
class InstagramStripRow extends HTMLElement {
  constructor() {
    super();
    this.items = Array.from(this.querySelectorAll('deferred-media'));
    this.currentIndex = -1;
    this.userInteracted = false;
  }

  connectedCallback() {
    if (this.items.length === 0 || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.items.forEach((mediaEl) => {
      const poster = mediaEl.querySelector('[id^="Deferred-Poster-"]');
      if (!poster) return;
      poster.addEventListener('click', () => {
        this.userInteracted = true;
        window.requestAnimationFrame(() => {
          const video = mediaEl.querySelector('video');
          if (video) video.muted = false;
        });
      });
    });

    // `this` has `display: contents` (so it doesn't disturb the row's flex
    // layout) and therefore generates no box of its own — IntersectionObserver
    // can't measure an element with no box, so it never fires on `this`.
    // Observe the actual row element instead, which does have real geometry.
    const target = this.querySelector('.instagram-strip__row') || this;

    this.observer = new IntersectionObserver((entries) => this.handleIntersect(entries), {
      threshold: 0.5,
    });
    this.observer.observe(target);
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }

  handleIntersect(entries) {
    if (this.userInteracted) return;
    const entry = entries[0];
    if (entry.isIntersecting) {
      if (this.currentIndex === -1) {
        this.playIndex(0);
      } else {
        const video = this.items[this.currentIndex]?.querySelector('video');
        if (video) video.play().catch(() => {});
      }
    } else {
      const video = this.items[this.currentIndex]?.querySelector('video');
      if (video) video.pause();
    }
  }

  playIndex(index) {
    if (this.userInteracted) return;
    this.currentIndex = index;
    const mediaEl = this.items[index];
    if (!mediaEl) return;

    const advance = () => {
      if (this.userInteracted) return;
      this.playIndex((index + 1) % this.items.length);
    };

    let video = mediaEl.querySelector('video');
    if (video) {
      video.muted = true;
      video.currentTime = 0;
      video.play().catch(() => {});
      video.addEventListener('ended', advance, { once: true });
      return;
    }

    if (typeof mediaEl.loadContent !== 'function') {
      advance();
      return;
    }

    mediaEl.loadContent(false);
    window.requestAnimationFrame(() => {
      video = mediaEl.querySelector('video');
      if (!video) {
        advance();
        return;
      }
      video.muted = true;
      video.addEventListener('ended', advance, { once: true });
    });
  }
}

if (!customElements.get('instagram-strip-row')) {
  customElements.define('instagram-strip-row', InstagramStripRow);
}
